import React, { useCallback, useMemo } from 'react';

import {
  RecordContextProvider,
  useGetOne,
  useRecordContext,
} from 'react-admin';

import {
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';

import { useNavigate } from 'react-router-dom';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import {
  get,
  isNil,
} from 'lodash';

import AnswerTitle, { DisplayValues } from './AnswerTitle';
import { ConflictFields } from '../../../constants/conflictFields';
import formAnswerConflictStatus from '../../../constants/formAnswerConflictStatus';
import { FieldStatus } from '../../../constants/fieldStatus';

const FieldWrapper = <T, U>({
  source,
  datapoint,
  identificationInfo,
  children,
  label,
  isComplexType,
  formatter,
}: {
  source: string;
  datapoint: ConflictFields | null;
  identificationInfo: unknown | undefined;
  children: React.ReactElement;
  label: string;
  isComplexType: boolean;
  formatter?: (value: T) => U;
}) => {
  const navigate = useNavigate();

  const kycForm = useRecordContext();

  const urlParamId = `${datapoint}/${kycForm?.entity?.id}/${kycForm?.entity?.type}${identificationInfo ? `/${JSON.stringify(identificationInfo)}` : ''}`;

  const { data } = useGetOne<{
    id: string;
    status: FieldStatus;
    selectedDatapointId?: string;
    selectedValue?: unknown;
    identificationInfo: unknown;
  }>('data-points/status', { id: urlParamId }, {
    enabled: !!datapoint && !!kycForm?.entity?.id && !!kycForm?.entity?.type,
  });

  const { data: sourceData, isLoading: sourceLoading } = useGetOne(
    `data-point-sources/datapoint/${data?.selectedDatapointId}/document`,
    { id: kycForm?.id },
    { enabled: !!data?.selectedDatapointId && !!kycForm?.id },
  );

  const status = (() => {
    if (!datapoint) return formAnswerConflictStatus.NO_DATAPOINT;
    if (data?.selectedDatapointId) {
      const areSameValues = !!sourceData || sourceLoading;

      if (areSameValues) return formAnswerConflictStatus.RESOLVED;
      return formAnswerConflictStatus.OTHER_VALUE_SELECTED;
    }
    return data?.status;
  })();

  const handelClick = useCallback(() => {
    const searchParams = new URLSearchParams();
    if (datapoint) searchParams.append('name', datapoint);
    if (identificationInfo) searchParams.append('identificationInfo', JSON.stringify(identificationInfo));
    navigate(`conflict-resolution/${kycForm?.entity?.type}/${kycForm?.entity?.id}?${searchParams}`, { state: { label } });
  }, [datapoint, identificationInfo, navigate, label, kycForm?.entity?.type, kycForm?.entity?.id]);

  const record = useMemo(() => ({
    value: formatter
      ? formatter(get(kycForm, source, null))
      : get(kycForm, source, null),
  }), [formatter, kycForm, source]);

  if (!status) return null;

  const shouldDisplayOtherSelectedValue = !isNil(data?.selectedValue)
    && status === formAnswerConflictStatus.OTHER_VALUE_SELECTED;

  return (
    <RecordContextProvider value={record}>
      <Card
        variant="outlined"
        sx={{
          m: 0,
          borderRadius: 1,
          borderColor: status === formAnswerConflictStatus.CONFLICT ? 'yellow.main' : '',
        }}
      >
        <CardContent sx={{ backgroundColor: 'background.paper', p: 6 }}>
          <Grid container spacing={1}>
            <Grid
              display="flex"
              alignItems="center"
              minHeight={40}
              size={10}
            >
              {!isComplexType && (
                <AnswerTitle
                  label={label}
                  status={status}
                  selectedValue={data?.selectedValue}
                  shouldDisplayOtherSelectedValue={shouldDisplayOtherSelectedValue}
                  datapoint={datapoint}
                >
                  {children}
                </AnswerTitle>
              )}
              {isComplexType && (
                <AnswerTitle
                  label={label}
                  status={status}
                  shouldDisplayOtherSelectedValue={false}
                  datapoint={datapoint}
                />
              )}
            </Grid>
            <Grid
              justifyContent="end"
              display="flex"
              size={2}
            >
              {status === formAnswerConflictStatus.CONFLICT && (
                <Button disableRipple endIcon={<ChevronRightIcon />} onClick={handelClick}>
                  <Typography color="primary" fontWeight="bold">Resolve conflict</Typography>
                </Button>
              )}
              {(
                status !== formAnswerConflictStatus.CONFLICT
                && status !== formAnswerConflictStatus.NO_DATAPOINT
              ) && (
                <IconButton disableRipple onClick={handelClick}>
                  <ChevronRightIcon color="primary" />
                </IconButton>
              )}
            </Grid>
            {isComplexType && (
              <>
                <Grid size={12}>
                  <DisplayValues
                    selectedValue={false}
                    label=""
                    shouldDisplayOtherSelectedValue={shouldDisplayOtherSelectedValue}
                    datapoint={datapoint}
                  >
                    {children}
                  </DisplayValues>
                </Grid>
                {shouldDisplayOtherSelectedValue && (
                  <Grid
                    my={2}
                    size={12}
                  >
                    <DisplayValues
                      selectedValue={data?.selectedValue}
                      label="Currently selected value"
                      shouldDisplayOtherSelectedValue={false}
                      datapoint={datapoint}
                    >
                      {children}
                    </DisplayValues>
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </CardContent>
      </Card>
    </RecordContextProvider>
  );
};

const Answer = <T = unknown, U = unknown>({
  label,
  children,
  source,
  datapoint,
  identificationInfo,
  formatter,
  isComplexType = false,
}: {
  label: string;
  source: string;
  children: React.ReactElement;
  datapoint: ConflictFields | null;
  identificationInfo: unknown | undefined;
  formatter?: (value: T) => U;
  isComplexType?: boolean;
}) => (
  <Grid size={12}>
    <FieldWrapper
      isComplexType={isComplexType}
      label={label}
      source={source}
      datapoint={datapoint}
      identificationInfo={identificationInfo}
      formatter={formatter}
    >
      {children}
    </FieldWrapper>
  </Grid>
);

export default Answer;
